import { PaymentPricing } from '@/types/payment-account';
import { PAYMENT_METHOD } from '@/types/payment-method';
import { formatCurrency } from '@/utils/format-currency';
import { formatFeePercent, simulatePayment } from '@/utils/payment-simulator';
import {
  formatBoletoRange,
  formatCardBrands,
  formatFeeLine,
  formatPixLimit,
  formatSettlementLine,
} from './shared';

/**
 * Os artigos da central "Receber pela Controlapag" (rota pública
 * `/ajuda/recebimentos`). Título em forma de pergunta (vira H2 na tela e
 * `Question` no `FAQPage`), corpo sempre em função de `PaymentPricing`: a
 * tela renderiza primeiro com a reserva de `facts.ts` e depois com os
 * números vivos da API.
 */
export interface PaymentHelpArticle {
  slug: string;
  title: string;
  body: (pricing: PaymentPricing) => string;
}

/** Valor do exemplo de taxa. */
export const HELP_EXAMPLE_AMOUNT = 100;

/** Quanto chega de uma cobrança de `amount` em cada forma, pela mesma conta do simulador do app. */
export function exampleNetAmounts(pricing: PaymentPricing, amount = HELP_EXAMPLE_AMOUNT) {
  const net = (method: PAYMENT_METHOD.PIX | PAYMENT_METHOD.CREDIT_CARD | PAYMENT_METHOD.BANK_SLIP) =>
    simulatePayment({ amount, method, pricing }).netAmount;
  return {
    pix: net(PAYMENT_METHOD.PIX),
    card: net(PAYMENT_METHOD.CREDIT_CARD),
    boleto: net(PAYMENT_METHOD.BANK_SLIP),
  };
}

export const PAYMENT_HELP_ARTICLES: PaymentHelpArticle[] = [
  {
    slug: 'como-funciona',
    title: 'Como funciona o recebimento pelo link?',
    body: () =>
      'O recebimento pelo link é um recurso dos planos pagos: o cliente paga pelo link da cobrança e ela dá baixa sozinha. Você ativa uma vez e escolhe as formas aceitas: Pix, cartão e, se você ligar, boleto. O dinheiro segue para a sua conta bancária. Sem ativar, nada muda: o cliente paga direto a você, e você toca em "Confirmar recebimento" na cobrança e confirma. Os dois modos convivem, e dá para trocar quando quiser.',
  },
  {
    slug: 'taxas-e-exemplos',
    title: 'Quanto custa receber pelo link?',
    body: (pricing) => {
      const example = exampleNetAmounts(pricing);
      return `A taxa é por pagamento e sai do valor que você recebe: ${formatFeeLine(pricing)}. Uma cobrança de ${formatCurrency(HELP_EXAMPLE_AMOUNT)} chega como ${formatCurrency(example.pix)} no Pix, ${formatCurrency(example.card)} no cartão e ${formatCurrency(example.boleto)} no boleto. A Controlapag fica com ${formatFeePercent(pricing.marginPercent)} do pagamento: a taxa é toda de quem processa o pagamento. No app, a tela "Como funciona" simula qualquer valor, e cada cobrança paga mostra o bruto, a taxa e o líquido.`;
    },
  },
  {
    slug: 'quando-o-dinheiro-cai',
    title: 'Quando o dinheiro cai na minha conta?',
    body: (pricing) =>
      `Depois que o cliente paga, o valor fica "liberando" por um tempo antes do repasse: ${formatSettlementLine(pricing)}. Quando libera, o repasse vai para o seu banco automaticamente. Os repasses são diários; não existe repasse manual nem na hora. Em Recebimentos você acompanha o que está a caminho, o que está liberando e o que já caiu.`,
  },
  {
    slug: 'limites-e-bandeiras',
    title: 'Quais cartões e valores o link aceita?',
    body: (pricing) =>
      `Cartão: ${formatCardBrands(pricing.cardBrands)}, só à vista, sem parcelamento. Cartão de outra bandeira não passa pelo link: o cliente pode pagar direto a você, e você confirma o recebimento no app. Pix: ${formatPixLimit(pricing)}. Boleto, se você ligar: ${formatBoletoRange(pricing)}; fora dessa faixa, a opção não aparece para o cliente.`,
  },
  {
    slug: 'reembolso-e-contestacao',
    title: 'Como devolver um pagamento ou responder a uma contestação?',
    body: () =>
      'Pix e cartão você devolve pelo próprio app, no total ou em parte, direto na cobrança; o dinheiro sai da sua conta como qualquer devolução. Boleto pago não tem devolução automática: combine com o cliente e devolva por fora, por Pix. Contestação é quando o cliente pede ao banco dele para cancelar a compra no cartão; se acontecer, você vê o prazo e o que responder na própria cobrança.',
  },
  {
    slug: 'por-que-pedimos-esses-dados',
    title: 'Por que a Controlapag pede CPF e conta bancária?',
    body: () =>
      'Quem processa o pagamento é obrigado por lei a confirmar quem recebe, por isso pede CPF, data de nascimento, endereço e conta bancária. Esses dados passam pelos nossos servidores só para serem enviados a quem processa o pagamento, uma única vez, e não ficam guardados por nós. Na tela, mostramos só o CPF mascarado, o banco e os últimos dígitos da conta.',
  },
  {
    slug: 'voltar-para-o-manual',
    title: 'Como desligar o recebimento pelo link?',
    body: () =>
      'Dá para desligar quando quiser, sem perder nada. As cobranças em aberto voltam a ser pagas direto a você, e o que já estava liberando continua caindo na sua conta até o fim do prazo. Para reativar, é só ligar de novo.',
  },
  {
    slug: 'equipe',
    title: 'Como funciona o recebimento pelo link numa equipe?',
    body: () =>
      'No caixa único, só o dono do negócio ativa, e todos os profissionais recebem na mesma conta. Por profissional, cada um ativa a própria conta, se quiser, e o dono vê quem já ativou. Para trocar de um modo para o outro com contas já ativas, é preciso desligar o recebimento pelo link antes, para nenhum pagamento cair na conta errada.',
  },
  {
    slug: 'impostos',
    title: 'O dinheiro recebido pelo link é receita minha?',
    body: () =>
      'Sim. O que você recebe pelo link é receita sua, como o que o cliente paga direto a você; a Controlapag só faz o pagamento chegar. O extrato de repasses em Recebimentos mostra bruto, taxa e líquido de cada período, para levar ao seu contador.',
  },
];
